import { fetchApi } from './client';

const API_BASE = process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000';

// Get user's organizations
export async function getUserOrganizations(userId: string, token: string) {
  const url = `${API_BASE}/api/v1/organisations/users/${userId}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    throw error;
  }
}

// Get organization details
export async function getOrganizationDetail(orgId: string, token: string) {
  const url = `${API_BASE}/api/v1/organisations/${orgId}`;
  
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching organization details:', error);
    throw error;
  }
}

// Create new organization
export async function createOrganization(data: { name: string, userId: string }, token: string) {
  const url = `${API_BASE}/api/v1/organisations`;
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
}

// Upload file and get URL
export async function uploadFile(file: File, token: string): Promise<string> {
  const url = `${API_BASE}/api/v1/file-upload/single`;
  
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'organizations'); // Specify folder for organization files

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Upload failed: ${response.statusText}`);
    }

    const responseData = await response.json();
    console.log('🔍 [Organizations API] Upload response:', responseData);
    
    // Try multiple possible URL locations in the response (same as user.ts)
    let imageUrl = '';
    if (responseData.data?.url) {
      imageUrl = responseData.data.url;
    } else if (responseData.url) {
      imageUrl = responseData.url;
    } else if (responseData.data?.data?.url) {
      imageUrl = responseData.data.data.url;
    } else if (responseData.data?.fileUrl) {
      imageUrl = responseData.data.fileUrl;
    } else if (responseData.fileUrl) {
      imageUrl = responseData.fileUrl;
    }
    
    if (!imageUrl) {
      throw new Error('No URL returned from upload API');
    }
    
    console.log('✅ [Organizations API] Upload successful:', imageUrl);
    return imageUrl;
  } catch (error) {
    console.error('❌ [Organizations API] Upload error:', error);
    throw error;
  }
}

// Update organization
export async function updateOrganization(orgId: string, data: any, token: string) {
  const url = `${API_BASE}/api/v1/organisations/${orgId}`;
  
  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
}

// TypeScript interfaces
export interface Organization {
  id: string;
  name: string;
  description: string;
  image: string;
  banner: string;
  showOnSite: boolean;
  approvalStatus: string;
  users: {
    userId: string;
    role: string;
    joinedAt: string;
  }[];
  website: string;
  phone: string;
  email: string;
  address: {
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    fullAddress: string;
  };
  operatingHours: {
    [key: string]: {
      open: string;
      close: string;
      isClosed: boolean;
    };
  };
  servicesOffered: any[];
  servicesWithSpecs: any[];
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: Organization | Organization[];
  meta?: any;
}
